/* Replace Sahara's existing cart-recommendations.js, rather than loading both. */
(() => {
  if (customElements.get('cart-recommendations')) return;
  const root = window.Shopify?.routes?.root || '/';
  const ttl = 30 * 60 * 1000;
  class CartRecommendations extends HTMLElement {
    constructor() { super(); this.slider = null; this.generation = 0; }
    connectedCallback() { this.performRecommendations(); }
    disconnectedCallback() {
      this.generation++;
      this.controller?.abort();
      this.slider?.destroy(true, true);
      this.slider = null;
    }
    async performRecommendations() {
      const wrapper = this.querySelector('[data-recommendations]');
      if (!wrapper) return;
      this.controller?.abort();
      const controller = this.controller = new AbortController();
      const generation = ++this.generation;
      const active = () => this.isConnected && this.generation === generation;
      const request = async (path, params = {}, html = false) => {
        const url = new URL(root + path, location.origin);
        Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
        const localController = new AbortController();
        const cancel = () => localController.abort();
        controller.signal.addEventListener('abort', cancel, { once: true });
        if (controller.signal.aborted) cancel();
        const timeout = setTimeout(cancel, 8000);
        try {
          const response = await fetch(url, { signal: localController.signal });
          if (!response.ok) throw new Error(`${path}: HTTP ${response.status}`);
          return html ? await response.text() : await response.json();
        } finally {
          clearTimeout(timeout);
          controller.signal.removeEventListener('abort', cancel);
        }
      };
      const optional = async callback => {
        try { return await callback(); }
        catch (error) {
          if (controller.signal.aborted) throw error;
          console.warn('[Cart intent recommendations]', error);
          return [];
        }
      };
      try {
        // Retain support for existing uses of the old snippet elsewhere.
        if (!this.hasAttribute('data-intent-cart')) {
          if (!this.dataset.url) return;
          const response = await fetch(this.dataset.url, { signal: controller.signal });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const incoming = parsed.querySelector('[data-recommendations]');
          if (!active() || !incoming?.innerHTML.trim()) return;
          wrapper.innerHTML = incoming.innerHTML;
          this.classList.remove('hidden'); this.initSlider(); return;
        }
        const cart = await request('cart.js');
        if (!active()) return;
        const excluded = new Set(cart.items.map(item => String(item.product_id)));
        const seeds = [...excluded].slice(0, 3);
        if (!seeds.length) { this.classList.add('hidden'); return; }
        let state = { views: [], search: null };
        try {
          const stored = JSON.parse(sessionStorage.getItem('herzens-intent-v1'));
          if (stored && Array.isArray(stored.views)) state = stored;
        } catch (_) {}
        const limit = Math.max(1, Math.min(8, Number(this.dataset.limit) || 4));
        const selected = new Map();
        const add = products => {
          for (const product of products) {
            if (selected.size >= limit) break;
            const id = String(product.id);
            const image = typeof product.image === 'string' ? product.image :
              (typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.url);
            let hasImage = false;
            try { hasImage = !!image && ['http:', 'https:'].includes(new URL(image, location.origin).protocol); } catch (_) {}
            if (excluded.has(id) || selected.has(id) || product.available !== true || !hasImage ||
                !/^[a-zA-Z0-9_-]+$/.test(product.handle || '')) continue;
            selected.set(id, product);
          }
        };
        const recommendations = (id, intent) => optional(async () =>
          (await request('recommendations/products.json', { product_id: id, intent, limit: 10 })).products || []);
        const mix = groups => {
          for (let index = 0; index < 10 && selected.size < limit; index++) {
            groups.forEach(group => { if (group[index]) add([group[index]]); });
          }
        };
        // Cart complements first. Search and browsing fill the remaining slots.
        mix(await Promise.all(seeds.map(id => recommendations(id, 'complementary'))));
        if (selected.size < limit && state.search?.q && Date.now() - state.search.at < ttl) {
          add(await optional(async () => (await request('search/suggest.json', {
            q: state.search.q, 'resources[type]': 'product', 'resources[limit]': 10,
            'resources[options][unavailable_products]': 'hide'
          })).resources?.results?.products || []));
        }
        if (selected.size < limit) {
          const recent = state.views.filter(view => view && /^\d+$/.test(String(view.id)) &&
            Date.now() - view.at < ttl && !excluded.has(String(view.id))).slice(0, 3);
          mix(await Promise.all(recent.map(view => recommendations(view.id, 'related'))));
        }
        if (selected.size < limit) mix(await Promise.all(seeds.map(id => recommendations(id, 'related'))));
        const cards = await Promise.all([...selected.values()].map(product => optional(async () => {
          const html = await request('products/' + encodeURIComponent(product.handle), { view: 'card' }, true);
          const slide = document.createElement('div');
          slide.className = 'swiper-slide card-product-slider__slide cart-recommendations__slide';
          slide.innerHTML = html;
          if (!slide.querySelector('card-product') || !slide.querySelector('.card-product')) return [];
          return [slide];
        })));
        if (!active()) return;
        const slides = cards.flat();
        // Avoid duplicate form IDs when these products also appear on the page.
        const prefix = 'cart-intent-' + (crypto.randomUUID?.() || Date.now());
        slides.forEach((slide, index) => {
          const ids = new Map();
          slide.querySelectorAll('[id]').forEach(element => {
            const old = element.id; const next = `${prefix}-${index}-${old}`;
            ids.set(old, next); element.id = next;
          });
          slide.querySelectorAll('*').forEach(element => {
            ['for', 'form', 'aria-controls', 'aria-labelledby', 'aria-describedby', 'aria-owns'].forEach(attr => {
              const value = element.getAttribute(attr);
              if (value) element.setAttribute(attr, value.split(/\s+/).map(id => ids.get(id) || id).join(' '));
            });
            const href = element.getAttribute('href');
            if (href?.startsWith('#') && ids.has(href.slice(1))) element.setAttribute('href', '#' + ids.get(href.slice(1)));
          });
        });
        this.slider?.destroy(true, true); this.slider = null;
        wrapper.replaceChildren(...slides);
        this.classList.toggle('hidden', slides.length === 0);
        if (slides.length) this.initSlider();
      } catch (error) {
        if (active() && !controller.signal.aborted) console.warn('[Cart intent recommendations]', error);
      }
    }
    initSlider() {
      if (typeof Swiper === 'undefined') return;
      this.slider = new Swiper(this.querySelector('.swiper'), {
        slidesPerView: 'auto', spaceBetween: 8, watchOverflow: true,
        observer: true, observeParents: true
      });
    }
  }
  customElements.define('cart-recommendations', CartRecommendations);
})();